import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircle from '@mui/icons-material/CheckCircle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { Button } from '../button/button';

interface SymptomAssessmentSummaryProps {
  playerFirstName: string;
  userFirstName: string;
  isGuardianUser: boolean;
  guardianEmails: string[];
  score: number;
  totalSteps: number;
  summaryCloseAction: () => void;
}

export function SymptomAssessmentSummary({
  playerFirstName,
  userFirstName,
  isGuardianUser,
  guardianEmails,
  score,
  totalSteps,
  summaryCloseAction,
}: SymptomAssessmentSummaryProps) {
  const { t } = useTranslation();

  return (
    <Grid
      container
      spacing={2}
      direction="column"
      justifyContent="space-between"
      pt={6}
    >
      <Grid item sx={{ textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_TITLE')}
        </Typography>
      </Grid>

      <Grid item sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="subtitle2">
          {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_SCORE_TITLE', {
            playerFirstName,
          })}
        </Typography>
        <Typography variant="h6" color="primary" fontWeight="bold">
          {`${score}/${totalSteps}`}
        </Typography>
      </Grid>

      <Grid item sx={{ mt: 2, alignSelf: 'center', textAlign: 'center' }}>
        {score > 0 && (
          <>
            <Typography variant="body1" fontWeight="bold">
              {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_RECOMMEND_TREATMENT')}
            </Typography>
            <WarningIcon color="warning" fontSize="large" sx={{ mt: 2 }} />
          </>
        )}

        {score === 0 && (
          <>
            <Typography variant="body1" fontWeight="bold">
              {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_ALL_GOOD')}
            </Typography>
            <CheckCircle color="primary" fontSize="large" sx={{ mt: 2 }} />
          </>
        )}

        <Typography variant="body1">
          {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_THANKS', { userFirstName })}
        </Typography>

        <List
          sx={{
            listStyleType: 'disc',
            pl: 4,
            '& .MuiListItem-root': {
              display: 'list-item',
              mt: -2,
            },
            mt: 1,
          }}
        >
          <ListItem>
            <Typography variant="subtitle2">
              {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_UPDATED_INCIDENT', {
                playerFirstName,
              })}
            </Typography>
          </ListItem>
          {!isGuardianUser && guardianEmails.length > 0 && (
            <ListItem>
              <Typography variant="subtitle2">
                {t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_SENT_RESULTS', {
                  guardianEmails: guardianEmails.join(', '),
                })}
              </Typography>
            </ListItem>
          )}
        </List>
      </Grid>

      <Grid item textAlign="center" sx={{ mt: 2 }}>
        <Button
          size="small"
          variant="contained"
          type="button"
          color="secondary"
          fullWidth
          onClick={summaryCloseAction}
          label={t('symptom.SYMPTOM_ASSESSMENT_SUMMARY_CLOSE')}
        />
      </Grid>
    </Grid>
  );
}
